import { useState, useCallback } from "react";

const LIMITS = {
    IMAGE: 5,
    VIDEO: 16,
    DOCUMENT: 100
};

const MIN_WIDTH = 500;
const MIN_HEIGHT = 500;
const MAX_WIDTH = 4096;
const MAX_HEIGHT = 4096;

const MIN_RATIO = 0.5;
const MAX_RATIO = 2.5;

export const useFileUploadHook = () => {

    const [error, setError] = useState(null);
    const [fileType, setFileType] = useState(null);
    const [dimensions, setDimensions] = useState(null);

    const validateImageDimensions = (file) => {
        return new Promise((resolve) => {
            const img = new Image();
            const objectUrl = URL.createObjectURL(file);

            img.onload = () => {
                const width = img.width;
                const height = img.height;
                URL.revokeObjectURL(objectUrl);

                const ratio = width / height;

                if (width < MIN_WIDTH)
                    return resolve({ error: `Minimum width is ${MIN_WIDTH}px` });

                if (height < MIN_HEIGHT)
                    return resolve({ error: `Minimum height is ${MIN_HEIGHT}px` });

                if (width > MAX_WIDTH || height > MAX_HEIGHT)
                    return resolve({ error: `Image resolution too large (Max 4096x4096)` });

                if (ratio < MIN_RATIO || ratio > MAX_RATIO)
                    return resolve({
                        error: `Aspect ratio should be between 1:2 and 2.5:1 (Current: ${ratio.toFixed(2)}:1)`
                    });

                resolve({ error: null, width, height });
            };

            img.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                resolve({ error: "Unable to read image dimensions" });
            };

            img.src = objectUrl;
        });
    };

    const validate = useCallback(async (file) => {

        if (!file) {
            setError("File is required");
            return { isValid: false, error: "File is required" };
        }

        const extension = file.name.split(".").pop()?.toLowerCase();
        const mimeType = file.type;
        const sizeInMB = file.size / (1024 * 1024);

        if (!mimeType) {
            setError("Unable to detect file type");
            return { isValid: false, error: "Unable to detect file type" };
        }

        const blockedExtensions = ["exe", "apk", "bat", "sh", "js"];
        if (blockedExtensions.includes(extension)) {
            setError("This file type is not allowed");
            return { isValid: false, error: "This file type is not allowed" };
        }

        if (mimeType.startsWith("image/")) {

            if (!["jpg", "jpeg", "png"].includes(extension)) {
                setError("Only JPG and PNG images are allowed");
                return { isValid: false, error: "Only JPG and PNG images are allowed" };
            }

            if (sizeInMB > LIMITS.IMAGE) {
                setError(`Image must be less than ${LIMITS.IMAGE}MB`);
                return { isValid: false, error: `Image must be less than ${LIMITS.IMAGE}MB` };
            }

            const dimensionCheck = await validateImageDimensions(file);
            if (dimensionCheck.error) {
                setError(dimensionCheck.error);
                return { isValid: false, error: dimensionCheck.error };
            }

            setError(null);
            setFileType("IMAGE");
            setDimensions({
                width: dimensionCheck.width,
                height: dimensionCheck.height
            });

            return {
                isValid: true,
                fileType: "IMAGE",
                dimensions: dimensionCheck
            };
        }

        if (mimeType.startsWith("video/")) {

            if (!["mp4", "3gp"].includes(extension)) {
                setError("Only MP4 and 3GP videos are allowed");
                return { isValid: false, error: "Only MP4 and 3GP videos are allowed" };
            }

            if (sizeInMB > LIMITS.VIDEO) {
                setError(`Video must be less than ${LIMITS.VIDEO}MB`);
                return { isValid: false, error: `Video must be less than ${LIMITS.VIDEO}MB` };
            }

            setError(null);
            setFileType("VIDEO");
            setDimensions(null);

            return { isValid: true, fileType: "VIDEO" };
        }

        if (mimeType === "application/pdf") {

            if (extension !== "pdf") {
                setError("Only PDF documents are allowed");
                return { isValid: false, error: "Only PDF documents are allowed" };
            }

            if (sizeInMB > LIMITS.DOCUMENT) {
                setError(`PDF must be less than ${LIMITS.DOCUMENT}MB`);
                return { isValid: false, error: `PDF must be less than ${LIMITS.DOCUMENT}MB` };
            }

            setError(null);
            setFileType("DOCUMENT");
            setDimensions(null);

            return { isValid: true, fileType: "DOCUMENT" };
        }
        setError("Unsupported file type for WhatsApp header");
        return { isValid: false, error: "Unsupported file type for WhatsApp header" };

    }, []);

    const reset = () => {
        setError(null);
        setFileType(null);
        setDimensions(null);
    };

    return {
        validate,
        error,
        fileType,
        dimensions,
        reset
    };
};
